import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { normalizeImageUrl } from "@/utils/imageUtils";
import ClubProgresoCard from "@/components/ClubProgresoCard";
import {
  trackAddToCart,
  trackPurchase,
  trackInitiateCheckout,
  trackCatalogView,
  trackWhatsAppClick,
} from "@/services/metaPixel";
import {
  ShoppingCart,
  ClipboardList,
  Plus,
  Minus,
  Trash2,
  Phone,
  MapPin,
  Calendar,
  Clock,
  CreditCard,
  Banknote,
  Building2,
  Truck,
  ChevronRight,
  Copy,
  Check,
  MessageCircle,
  Star,
  Users,
  Instagram,
  ExternalLink,
} from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Componente de Producto
const ProductCard = ({ producto, cantidad, onAdd, onUpdate }) => {
  const enCarrito = cantidad > 0;
  const imagenUrl = normalizeImageUrl(producto.imagen_url);
  
  return (
    <div 
      className="bg-white rounded-2xl overflow-hidden border border-[#E5D4B3] shadow-[0_8px_30px_rgb(74,44,10,0.08)] hover:-translate-y-1 hover:shadow-lg transition-all duration-300"
      data-testid={`product-card-${producto.id}`}
    >
      <div className="aspect-square bg-[#EDD9C0] relative overflow-hidden">
        {imagenUrl ? (
          <img 
            src={imagenUrl} 
            alt={producto.nombre}
            className="w-full h-full object-cover"
            onError={(e) => {
              e.target.style.display = 'none';
              if (e.target.nextSibling) e.target.nextSibling.style.display = 'flex';
            }}
          />
        ) : null}
        <div className={`absolute inset-0 flex items-center justify-center ${imagenUrl ? 'hidden' : 'flex'}`}>
          <div className="w-full h-full flex items-center justify-center">
            <span className="text-6xl">🥮</span>
          </div>
        </div>
        {producto.destacado && (
          <span className="absolute top-3 left-3 bg-[#C8902A] text-white text-xs font-medium px-2 py-1 rounded-full flex items-center gap-1">
            <Star className="w-3 h-3" /> Destacado
          </span>
        )}
      </div>
      <div className="p-4">
        <h3 className="font-['Playfair_Display'] text-lg text-[#4A2C0A] font-semibold tracking-tight">
          {producto.nombre}
        </h3>
        <p className="text-sm text-[#4A2C0A]/70 font-['Poppins'] line-clamp-1 mt-1">
          {producto.descripcion || "Producto artesanal"}
        </p>
        <p className="font-['Poppins'] text-xl font-bold text-[#C8902A] mt-2">
          ${producto.precio.toLocaleString("es-CO")}
        </p>
        
        {enCarrito ? (
          <div className="flex items-center justify-center gap-3 mt-3">
            <button 
              onClick={() => onUpdate(producto.id, cantidad - 1)}
              className="w-10 h-10 rounded-full bg-[#FBF5E8] border border-[#E5D4B3] flex items-center justify-center text-[#4A2C0A] hover:bg-[#E5D4B3] active:scale-95 transition-all"
              data-testid={`product-minus-${producto.id}`}
            >
              <Minus className="w-4 h-4" />
            </button>
            <span className="font-['Poppins'] font-semibold text-lg text-[#4A2C0A] w-8 text-center">
              {cantidad}
            </span>
            <button 
              onClick={() => onUpdate(producto.id, cantidad + 1)}
              className="w-10 h-10 rounded-full bg-[#4A2C0A] text-white flex items-center justify-center hover:bg-[#4A2C0A]/90 active:scale-95 transition-all"
              data-testid={`product-plus-${producto.id}`}
            >
              <Plus className="w-4 h-4" />
            </button>
          </div>
        ) : (
          <Button 
            onClick={() => onAdd(producto)}
            className="w-full mt-3 bg-[#C8902A] hover:bg-[#C8902A]/90 text-white font-['Poppins'] font-medium rounded-full h-11 active:scale-95 transition-all"
            data-testid={`product-add-${producto.id}`}
          >
            <Plus className="w-4 h-4 mr-2" /> Agregar
          </Button>
        )}
      </div>
    </div>
  );
};

// Componente del Carrito (Panel derecho / Tab móvil)
const CartPanel = ({ 
  cart, 
  updateQuantity, 
  removeItem, 
  config, 
  vendedor,
  onCheckout,
  checkoutLoading,
  form,
  setForm,
  ciudadesDisponibles
}) => {
  const [dateWarning, setDateWarning] = useState(null);
  
  const subtotal = cart.reduce((sum, item) => sum + item.precio * item.cantidad, 0);
  const costoDomicilio = form.tipo_entrega === "domicilio" ? (form.costo_domicilio || 0) : 0;
  const total = subtotal + costoDomicilio;
  
  // Fecha mínima (hoy) - Permitimos pedidos para hoy y mañana con alertas
  const today = new Date();
  const minDate = today.toISOString().split("T")[0];
  
  // Verificar si la fecha es hoy o mañana para mostrar alertas
  const checkDateWarning = (dateStr) => {
    if (!dateStr) {
      setDateWarning(null);
      return;
    }
    
    const selectedDate = new Date(dateStr + 'T12:00:00');
    const todayDate = new Date();
    todayDate.setHours(0, 0, 0, 0);
    
    const tomorrow = new Date(todayDate);
    tomorrow.setDate(tomorrow.getDate() + 1);
    
    const selectedDateOnly = new Date(selectedDate);
    selectedDateOnly.setHours(0, 0, 0, 0);
    
    if (selectedDateOnly.getTime() === todayDate.getTime()) {
      setDateWarning({
        type: "urgent",
        message: "Pedido para HOY - Sujeto a disponibilidad inmediata. Te contactaremos para confirmar."
      });
    } else if (selectedDateOnly.getTime() === tomorrow.getTime()) {
      setDateWarning({
        type: "notice",
        message: "Pedido para MAÑANA - Confirmaremos disponibilidad a la brevedad."
      });
    } else {
      setDateWarning(null);
    }
  };
  
  // Handler para cambio de fecha
  const handleDateChange = (e) => {
    const newDate = e.target.value;
    setForm({...form, fecha_entrega: newDate});
    checkDateWarning(newDate);
  };
  
  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Validación del carrito
    if (cart.length === 0) {
      toast.error("Agrega al menos un producto");
      return;
    }
    
    // Validaciones mínimas obligatorias
    if (!form.cliente_nombre?.trim()) {
      toast.error("Por favor ingresa tu nombre");
      return;
    }
    
    if (!form.cliente_telefono?.trim()) {
      toast.error("Por favor ingresa un teléfono de contacto");
      return;
    }
    
    if (!form.fecha_entrega) {
      toast.error("Por favor selecciona una fecha de entrega");
      return;
    }
    
    // Si es domicilio, la ciudad y dirección son necesarias
    if (form.tipo_entrega === "domicilio") {
      if (!form.ciudad?.trim()) {
        toast.error("Por favor selecciona una ciudad de entrega");
        return;
      }
      if (!form.direccion_entrega?.trim()) {
        toast.error("Por favor ingresa la dirección de entrega");
        return;
      }
    }
    
    if (!form.metodo_pago) {
      toast.error("Por favor selecciona un método de pago");
      return;
    }
    
    // Validar datos de factura electrónica (DIAN)
    if (form.requiere_factura) {
      const f = form.factura || {};
      if (!f.numero_documento?.trim()) {
        toast.error("Factura electrónica: ingresa el número de documento");
        return;
      }
      if (!f.razon_social?.trim()) {
        toast.error("Factura electrónica: ingresa el nombre o razón social");
        return;
      }
      if (!f.email_facturacion?.trim() || !f.email_facturacion.includes("@")) {
        toast.error("Factura electrónica: ingresa un email válido");
        return;
      }
      if (!f.direccion_fiscal?.trim()) {
        toast.error("Factura electrónica: ingresa la dirección fiscal");
        return;
      }
      if (!f.ciudad_fiscal?.trim()) {
        toast.error("Factura electrónica: ingresa la ciudad");
        return;
      }
    }
    
    // Todo OK, proceder con el checkout
    onCheckout(form, total);
  };
  
  const metodoPagoOptions = [
    { value: "efectivo_vendedor", icon: Banknote, label: "Pago al vendedor", desc: "Efectivo o transferencia cuando te contacte" },
    { value: "transferencia_baruj", icon: Building2, label: "Transferencia a Baruj Shefa", desc: "Nequi +57 319 6536874 o Bre-B Caja Social" },
    { value: "contra_entrega", icon: Truck, label: "Contra entrega", desc: "Paga al momento de recibir tu pedido" },
  ];
  
  if (cart.length === 0) {
    return (
      <div className="flex flex-col h-full overflow-y-auto">
        {/* Empty Cart Message */}
        <div className="flex flex-col items-center justify-center p-6 text-center border-b border-[#E5D4B3]">
          <ShoppingCart className="w-12 h-12 text-[#E5D4B3] mb-3" />
          <h3 className="font-['Playfair_Display'] text-lg text-[#4A2C0A] mb-1">Tu carrito está vacío</h3>
          <p className="font-['Poppins'] text-[#4A2C0A]/70 text-xs">
            Selecciona productos del catálogo para comenzar tu pedido
          </p>
        </div>
        
        {/* Quick Actions */}
        <div className="flex-1 p-4 space-y-4">
          {/* Club de la Abuela - activo */}
          <div className="bg-gradient-to-br from-[#C8902A]/10 to-[#C8902A]/5 rounded-2xl p-4 border border-[#C8902A]/20">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 bg-[#C8902A]/20 rounded-xl flex items-center justify-center flex-shrink-0">
                <Star className="w-5 h-5 text-[#C8902A]" />
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <h4 className="font-['Playfair_Display'] text-[#4A2C0A] font-semibold text-sm">
                    Club de la Abuela
                  </h4>
                  <span className="text-[9px] bg-green-600 text-white px-1.5 py-0.5 rounded-full font-['Poppins'] uppercase tracking-wider">
                    Nuevo
                  </span>
                </div>
                <p className="font-['Poppins'] text-[#4A2C0A]/70 text-xs mb-3">
                  Tus compras suben de nivel y desbloquean regalos y descuentos
                </p>
                <a 
                  href="/club"
                  data-testid="sidebar-club-link"
                  className="block text-center w-full bg-[#C8902A] hover:bg-[#b07e22] text-white font-['Poppins'] text-xs font-medium py-2.5 rounded-full transition-colors"
                >
                  Consultar mi nivel
                </a>
              </div>
            </div>
          </div>
          
          {/* Become a Seller */}
          <div className="bg-white rounded-2xl p-4 border border-[#E5D4B3]">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 bg-[#4A2C0A]/10 rounded-xl flex items-center justify-center flex-shrink-0">
                <Users className="w-5 h-5 text-[#4A2C0A]" />
              </div>
              <div className="flex-1">
                <h4 className="font-['Playfair_Display'] text-[#4A2C0A] font-semibold text-sm mb-1">
                  ¿Quieres vender?
                </h4>
                <p className="font-['Poppins'] text-[#4A2C0A]/70 text-xs mb-3">
                  Únete como vendedor Baruj Shefa y genera ingresos extras
                </p>
                <a 
                  href="/registro-vendedor"
                  className="block w-full bg-[#4A2C0A] hover:bg-[#4A2C0A]/90 text-white font-['Poppins'] text-xs font-medium py-2.5 rounded-full text-center transition-colors"
                >
                  Registrarme como Vendedor
                </a>
              </div>
            </div>
          </div>
          
          {/* Social Media */}
          <div className="bg-[#FBF5E8] rounded-2xl p-4">
            <h4 className="font-['Playfair_Display'] text-[#4A2C0A] font-semibold text-sm mb-3 text-center">
              Síguenos en redes sociales
            </h4>
            <div className="flex items-center justify-center gap-3">
              <a 
                href="https://www.instagram.com/tortadecuajada/"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 bg-gradient-to-br from-purple-500 via-pink-500 to-orange-400 rounded-xl flex items-center justify-center text-white hover:scale-105 transition-transform"
                title="Instagram"
              >
                <Instagram className="w-5 h-5" />
              </a>
              <a 
                href="https://www.tiktok.com/@tortadecuajada"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 bg-black rounded-xl flex items-center justify-center text-white hover:scale-105 transition-transform"
                title="TikTok"
              >
                <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
                </svg>
              </a>
              <a 
                href="https://wa.me/573196536874"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 bg-green-500 rounded-xl flex items-center justify-center text-white hover:scale-105 transition-transform"
                title="WhatsApp"
              >
                <MessageCircle className="w-5 h-5" />
              </a>
            </div>
            <p className="font-['Poppins'] text-[#4A2C0A]/50 text-[10px] text-center mt-2">
              @tortadecuajada
            </p>
          </div>
        </div>
        
        {/* Botón flotante de ayuda - También visible con carrito vacío */}
        <a
          href="https://wa.me/573196536874?text=Hola,%20necesito%20ayuda%20para%20hacer%20un%20pedido"
          target="_blank"
          rel="noopener noreferrer"
          className="fixed bottom-24 right-4 z-40 flex items-center gap-2 bg-[#25D366] text-white px-3 py-2 rounded-full shadow-lg hover:bg-[#20BD5A] active:scale-95 transition-all"
          data-testid="help-whatsapp-btn-empty"
        >
          <MessageCircle className="w-4 h-4" />
          <span className="font-['Poppins'] text-xs font-medium">¿Ayuda?</span>
        </a>
      </div>
    );
  }
  
  return (
    <div className="h-full flex flex-col">
      <div className="flex-1 overflow-y-auto p-4 md:p-6 space-y-4 pb-24">
        {/* Items del carrito */}
        <div className="space-y-3">
          {cart.map((item) => (
            <div key={item.id} className="bg-[#FBF5E8] rounded-xl p-3 flex items-center gap-3">
              <div className="w-14 h-14 bg-[#EDD9C0] rounded-lg flex items-center justify-center text-2xl flex-shrink-0">
                🥮
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-['Poppins'] font-medium text-[#4A2C0A] text-sm truncate">{item.nombre}</p>
                <p className="font-['Poppins'] text-[#C8902A] font-semibold text-sm">
                  ${item.precio.toLocaleString("es-CO")}
                </p>
              </div>
              <div className="flex items-center gap-2">
                <button 
                  onClick={() => updateQuantity(item.id, item.cantidad - 1)}
                  className="w-8 h-8 rounded-full border border-[#E5D4B3] flex items-center justify-center text-[#4A2C0A] hover:bg-white"
                >
                  <Minus className="w-3 h-3" />
                </button>
                <span className="font-['Poppins'] font-medium w-5 text-center">{item.cantidad}</span>
                <button 
                  onClick={() => updateQuantity(item.id, item.cantidad + 1)}
                  className="w-8 h-8 rounded-full bg-[#4A2C0A] text-white flex items-center justify-center"
                >
                  <Plus className="w-3 h-3" />
                </button>
              </div>
              <button 
                onClick={() => removeItem(item.id)}
                className="text-red-500 hover:text-red-600 p-1"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
        
        {/* Totales */}
        <div className="bg-white rounded-xl p-4 border border-[#E5D4B3] space-y-2">
          <div className="flex justify-between font-['Poppins'] text-sm">
            <span className="text-[#4A2C0A]/70">Subtotal</span>
            <span className="text-[#4A2C0A]">${subtotal.toLocaleString("es-CO")}</span>
          </div>
          <div className="flex justify-between font-['Poppins'] text-sm">
            <span className="text-[#4A2C0A]/70">Domicilio</span>
            <span className="text-[#4A2C0A]">
              {form.tipo_entrega === "domicilio" ? `$${costoDomicilio.toLocaleString("es-CO")}` : "Gratis"}
            </span>
          </div>
          <div className="border-t border-[#E5D4B3] pt-2 flex justify-between">
            <span className="font-['Playfair_Display'] text-lg text-[#4A2C0A]">Total</span>
            <span className="font-['Playfair_Display'] text-xl font-bold text-[#C8902A]">
              ${total.toLocaleString("es-CO")}
            </span>
          </div>
        </div>
        
        {/* Formulario de checkout */}
        <form id="checkout-form" onSubmit={handleSubmit} className="space-y-4">
          {/* Datos del cliente */}
          <div className="bg-white rounded-xl p-4 border border-[#E5D4B3] space-y-3">
            <h4 className="font-['Playfair_Display'] text-[#4A2C0A] font-semibold flex items-center gap-2">
              <Users className="w-4 h-4 text-[#C8902A]" /> Tus datos
            </h4>
            <div className="space-y-2">
              <Input
                placeholder="Nombre completo *"
                value={form.cliente_nombre}
                onChange={(e) => setForm({...form, cliente_nombre: e.target.value})}
                className="bg-[#FBF5E8] border-[#E5D4B3] font-['Poppins']"
                data-testid="input-cliente-nombre"
              />
              <Input
                placeholder="Teléfono o celular *"
                value={form.cliente_telefono}
                onChange={(e) => setForm({...form, cliente_telefono: e.target.value})}
                type="tel"
                className="bg-[#FBF5E8] border-[#E5D4B3] font-['Poppins']"
                data-testid="input-cliente-telefono"
              />
              <Input
                placeholder="Código Club de la Abuela (opcional) 👑"
                value={form.beneficio_codigo || ""}
                onChange={(e) => setForm({...form, beneficio_codigo: e.target.value.toUpperCase()})}
                className="bg-[#FBF5E8] border-[#E5D4B3] font-['Poppins']"
                data-testid="input-beneficio-codigo"
              />
              {form.beneficio_codigo && (
                <p className="text-[11px] text-green-700 font-['Poppins']">🎁 Tu beneficio del Club se validará y aplicará al confirmar el pedido (debe ser del mismo celular).</p>
              )}
            </div>
          </div>
          
          {/* Tipo de entrega */}
          <div className="bg-white rounded-xl p-4 border border-[#E5D4B3] space-y-3">
            <h4 className="font-['Playfair_Display'] text-[#4A2C0A] font-semibold flex items-center gap-2">
              <MapPin className="w-4 h-4 text-[#C8902A]" /> ¿Cómo recibes tu pedido?
            </h4>
            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={() => setForm({...form, tipo_entrega: "domicilio"})}
                className={`p-3 rounded-xl border-2 text-center transition-all ${
                  form.tipo_entrega === "domicilio" 
                    ? "border-[#C8902A] bg-[#C8902A]/5" 
                    : "border-[#E5D4B3] hover:border-[#C8902A]/50"
                }`}
              >
                <Truck className={`w-5 h-5 mx-auto mb-1 ${form.tipo_entrega === "domicilio" ? "text-[#C8902A]" : "text-[#4A2C0A]/50"}`} />
                <span className="font-['Poppins'] text-sm font-medium text-[#4A2C0A]">A domicilio</span>
              </button>
              <button
                type="button"
                onClick={() => setForm({...form, tipo_entrega: "punto"})}
                className={`p-3 rounded-xl border-2 text-center transition-all ${
                  form.tipo_entrega === "punto" 
                    ? "border-[#C8902A] bg-[#C8902A]/5" 
                    : "border-[#E5D4B3] hover:border-[#C8902A]/50"
                }`}
              >
                <MapPin className={`w-5 h-5 mx-auto mb-1 ${form.tipo_entrega === "punto" ? "text-[#C8902A]" : "text-[#4A2C0A]/50"}`} />
                <span className="font-['Poppins'] text-sm font-medium text-[#4A2C0A]">Recoger</span>
              </button>
            </div>
            
            {form.tipo_entrega === "domicilio" && (
              <div className="space-y-2 pt-2">
                {/* City Selector */}
                <div>
                  <select
                    value={form.ciudad}
                    onChange={(e) => {
                      const selectedCity = ciudadesDisponibles.find(c => c.ciudad === e.target.value);
                      setForm({
                        ...form, 
                        ciudad: e.target.value,
                        departamento: selectedCity?.departamento || "",
                        costo_domicilio: selectedCity?.costo_domicilio || 0
                      });
                    }}
                    className="w-full h-10 px-3 rounded-md bg-[#FBF5E8] border border-[#E5D4B3] font-['Poppins'] text-sm focus:outline-none focus:ring-2 focus:ring-[#C8902A] focus:border-transparent"
                    data-testid="city-select"
                  >
                    <option value="">Selecciona tu ciudad *</option>
                    {ciudadesDisponibles.map((ciudad, idx) => (
                      <option key={idx} value={ciudad.ciudad}>
                        {`${ciudad.ciudad}, ${ciudad.departamento} - Domicilio: $${ciudad.costo_domicilio.toLocaleString("es-CO")}`}
                      </option>
                    ))}
                  </select>
                  {form.ciudad && (
                    <p className="text-xs text-[#C8902A] mt-1 font-['Poppins']">
                      Domicilio: ${form.costo_domicilio.toLocaleString("es-CO")}
                    </p>
                  )}
                </div>
                
                {ciudadesDisponibles.length === 0 && (
                  <p className="text-xs text-amber-600 bg-amber-50 p-2 rounded-lg font-['Poppins']">
                    Cargando ciudades disponibles...
                  </p>
                )}
                
                <Input
                  placeholder="Dirección de entrega *"
                  value={form.direccion_entrega}
                  onChange={(e) => setForm({...form, direccion_entrega: e.target.value})}
                  className="bg-[#FBF5E8] border-[#E5D4B3] font-['Poppins']"
                  data-testid="address-input"
                />
                <Input
                  placeholder="Barrio (opcional)"
                  value={form.barrio_entrega}
                  onChange={(e) => setForm({...form, barrio_entrega: e.target.value})}
                  className="bg-[#FBF5E8] border-[#E5D4B3] font-['Poppins']"
                />
                <Input
                  placeholder="Referencia (opcional)"
                  value={form.notas_entrega}
                  onChange={(e) => setForm({...form, notas_entrega: e.target.value})}
                  className="bg-[#FBF5E8] border-[#E5D4B3] font-['Poppins']"
                />
              </div>
            )}
          </div>
          
          {/* Fecha y hora */}
          <div className="bg-white rounded-xl p-4 border border-[#E5D4B3] space-y-3">
            <h4 className="font-['Playfair_Display'] text-[#4A2C0A] font-semibold flex items-center gap-2">
              <Calendar className="w-4 h-4 text-[#C8902A]" /> ¿Cuándo lo quieres?
            </h4>
            <Input
              type="date"
              min={minDate}
              value={form.fecha_entrega}
              onChange={handleDateChange}
              className="bg-[#FBF5E8] border-[#E5D4B3] font-['Poppins']"
            />
            {/* Alerta de fecha urgente */}
            {dateWarning && (
              <div className={`rounded-lg p-3 flex items-start gap-2 ${
                dateWarning.type === "urgent" 
                  ? "bg-orange-50 border border-orange-200" 
                  : "bg-amber-50 border border-amber-200"
              }`}>
                <Clock className={`w-4 h-4 mt-0.5 flex-shrink-0 ${
                  dateWarning.type === "urgent" ? "text-orange-500" : "text-amber-500"
                }`} />
                <p className={`font-['Poppins'] text-xs ${
                  dateWarning.type === "urgent" ? "text-orange-700" : "text-amber-700"
                }`}>
                  {dateWarning.message}
                </p>
              </div>
            )}
            <div className="grid grid-cols-3 gap-2">
              {[
                { value: "manana", label: "Mañana", time: "8am-12m" },
                { value: "tarde", label: "Tarde", time: "12m-5pm" },
                { value: "noche", label: "Noche", time: "5pm-8pm" },
              ].map((franja) => (
                <button
                  key={franja.value}
                  type="button"
                  onClick={() => setForm({...form, franja_horaria: franja.value})}
                  className={`p-2 rounded-xl border-2 text-center transition-all ${
                    form.franja_horaria === franja.value 
                      ? "border-[#C8902A] bg-[#C8902A]/5" 
                      : "border-[#E5D4B3] hover:border-[#C8902A]/50"
                  }`}
                >
                  <Clock className={`w-4 h-4 mx-auto mb-1 ${form.franja_horaria === franja.value ? "text-[#C8902A]" : "text-[#4A2C0A]/50"}`} />
                  <span className="font-['Poppins'] text-xs font-medium text-[#4A2C0A] block">{franja.label}</span>
                  <span className="font-['Poppins'] text-[10px] text-[#4A2C0A]/50">{franja.time}</span>
                </button>
              ))}
            </div>
          </div>
          
          {/* Método de pago */}
          <div className="bg-white rounded-xl p-4 border border-[#E5D4B3] space-y-3">
            <h4 className="font-['Playfair_Display'] text-[#4A2C0A] font-semibold flex items-center gap-2">
              <CreditCard className="w-4 h-4 text-[#C8902A]" /> ¿Cómo pagas?
            </h4>
            <div className="space-y-2">
              {metodoPagoOptions.map((metodo) => (
                <button
                  key={metodo.value}
                  type="button"
                  onClick={() => setForm({...form, metodo_pago: metodo.value})}
                  className={`w-full p-3 rounded-xl border-2 text-left flex items-start gap-3 transition-all ${
                    form.metodo_pago === metodo.value 
                      ? "border-[#C8902A] bg-[#C8902A]/5" 
                      : "border-[#E5D4B3] hover:border-[#C8902A]/50"
                  }`}
                >
                  <metodo.icon className={`w-5 h-5 mt-0.5 flex-shrink-0 ${
                    form.metodo_pago === metodo.value ? "text-[#C8902A]" : "text-[#4A2C0A]/50"
                  }`} />
                  <div>
                    <span className="font-['Poppins'] text-sm font-medium text-[#4A2C0A] block">{metodo.label}</span>
                    <span className="font-['Poppins'] text-xs text-[#4A2C0A]/50">{metodo.desc}</span>
                  </div>
                </button>
              ))}
            </div>
            
            {form.metodo_pago === "transferencia_baruj" && (
              <Input
                placeholder="Número de referencia (opcional)"
                value={form.referencia_pago}
                onChange={(e) => setForm({...form, referencia_pago: e.target.value})}
                className="bg-[#FBF5E8] border-[#E5D4B3] font-['Poppins'] mt-2"
              />
            )}
          </div>
          
          {/* Factura Electrónica (DIAN) */}
          <div className="bg-white rounded-xl p-4 border border-[#E5D4B3]">
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={form.requiere_factura}
                onChange={(e) => setForm({...form, requiere_factura: e.target.checked})}
                className="w-5 h-5 accent-[#C8902A] rounded"
                data-testid="factura-checkbox"
              />
              <div>
                <p className="font-['Poppins'] font-semibold text-[#4A2C0A] text-sm">Requiero factura electrónica</p>
                <p className="text-xs text-[#4A2C0A]/60">Emitida según los requisitos de la DIAN</p>
              </div>
            </label>
            
            {form.requiere_factura && (
              <div className="mt-4 space-y-3 border-t border-dashed border-[#E5D4B3] pt-4" data-testid="factura-fields">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs text-[#4A2C0A]/60 font-['Poppins']">Tipo de persona *</label>
                    <select
                      value={form.factura.tipo_persona}
                      onChange={(e) => setForm({...form, factura: {...form.factura, tipo_persona: e.target.value, tipo_documento: e.target.value === "juridica" ? "NIT" : form.factura.tipo_documento}})}
                      className="w-full h-10 rounded-lg border border-[#E5D4B3] bg-[#FBF5E8] px-2 text-sm font-['Poppins']"
                      data-testid="factura-tipo-persona"
                    >
                      <option value="natural">Persona Natural</option>
                      <option value="juridica">Persona Jurídica</option>
                    </select>
                  </div>
                  <div>
                    <label className="text-xs text-[#4A2C0A]/60 font-['Poppins']">Tipo de documento *</label>
                    <select
                      value={form.factura.tipo_documento}
                      onChange={(e) => setForm({...form, factura: {...form.factura, tipo_documento: e.target.value}})}
                      className="w-full h-10 rounded-lg border border-[#E5D4B3] bg-[#FBF5E8] px-2 text-sm font-['Poppins']"
                      data-testid="factura-tipo-documento"
                    >
                      <option value="CC">Cédula de Ciudadanía</option>
                      <option value="NIT">NIT</option>
                      <option value="CE">Cédula de Extranjería</option>
                      <option value="PASAPORTE">Pasaporte</option>
                    </select>
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <div className={form.factura.tipo_documento === "NIT" ? "col-span-2" : "col-span-3"}>
                    <Input
                      placeholder={form.factura.tipo_documento === "NIT" ? "NIT (sin DV) *" : "Número de documento *"}
                      value={form.factura.numero_documento}
                      onChange={(e) => setForm({...form, factura: {...form.factura, numero_documento: e.target.value}})}
                      className="bg-[#FBF5E8] border-[#E5D4B3] font-['Poppins']"
                      data-testid="factura-numero-documento"
                    />
                  </div>
                  {form.factura.tipo_documento === "NIT" && (
                    <Input
                      placeholder="DV"
                      maxLength={1}
                      value={form.factura.dv}
                      onChange={(e) => setForm({...form, factura: {...form.factura, dv: e.target.value}})}
                      className="bg-[#FBF5E8] border-[#E5D4B3] font-['Poppins']"
                      data-testid="factura-dv"
                    />
                  )}
                </div>
                <Input
                  placeholder={form.factura.tipo_persona === "juridica" ? "Razón social *" : "Nombre completo *"}
                  value={form.factura.razon_social}
                  onChange={(e) => setForm({...form, factura: {...form.factura, razon_social: e.target.value}})}
                  className="bg-[#FBF5E8] border-[#E5D4B3] font-['Poppins']"
                  data-testid="factura-razon-social"
                />
                <Input
                  type="email"
                  placeholder="Email para recibir la factura *"
                  value={form.factura.email_facturacion}
                  onChange={(e) => setForm({...form, factura: {...form.factura, email_facturacion: e.target.value}})}
                  className="bg-[#FBF5E8] border-[#E5D4B3] font-['Poppins']"
                  data-testid="factura-email"
                />
                <div className="grid grid-cols-2 gap-3">
                  <Input
                    placeholder="Dirección fiscal *"
                    value={form.factura.direccion_fiscal}
                    onChange={(e) => setForm({...form, factura: {...form.factura, direccion_fiscal: e.target.value}})}
                    className="bg-[#FBF5E8] border-[#E5D4B3] font-['Poppins']"
                    data-testid="factura-direccion"
                  />
                  <Input
                    placeholder="Ciudad *"
                    value={form.factura.ciudad_fiscal}
                    onChange={(e) => setForm({...form, factura: {...form.factura, ciudad_fiscal: e.target.value}})}
                    className="bg-[#FBF5E8] border-[#E5D4B3] font-['Poppins']"
                    data-testid="factura-ciudad"
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <Input
                    placeholder="Teléfono (opcional)"
                    value={form.factura.telefono_facturacion}
                    onChange={(e) => setForm({...form, factura: {...form.factura, telefono_facturacion: e.target.value}})}
                    className="bg-[#FBF5E8] border-[#E5D4B3] font-['Poppins']"
                  />
                  <select
                    value={form.factura.responsable_iva}
                    onChange={(e) => setForm({...form, factura: {...form.factura, responsable_iva: e.target.value}})}
                    className="w-full h-10 rounded-lg border border-[#E5D4B3] bg-[#FBF5E8] px-2 text-sm font-['Poppins']"
                    data-testid="factura-responsable-iva"
                  >
                    <option value="no_responsable">No responsable de IVA</option>
                    <option value="responsable">Responsable de IVA</option>
                  </select>
                </div>
              </div>
            )}
          </div>
          
          {/* Notas */}
          <div className="bg-white rounded-xl p-4 border border-[#E5D4B3]">
            <Textarea
              placeholder="Notas adicionales (ej: entregar después de las 5pm, sin azúcar...)"
              value={form.notas}
              onChange={(e) => setForm({...form, notas: e.target.value})}
              maxLength={300}
              rows={2}
              className="bg-[#FBF5E8] border-[#E5D4B3] font-['Poppins'] text-sm resize-none"
            />
          </div>
        </form>
      </div>
      
      {/* Botón confirmar - Sticky en la parte inferior */}
      <div className="sticky bottom-0 left-0 right-0 p-4 bg-white border-t border-[#E5D4B3] shadow-[0_-4px_20px_rgba(0,0,0,0.1)]">
        <div className="flex items-center justify-between mb-3">
          <span className="font-['Playfair_Display'] text-lg text-[#4A2C0A]">Total a pagar</span>
          <span className="font-['Playfair_Display'] text-2xl font-bold text-[#C8902A]">
            ${total.toLocaleString("es-CO")}
          </span>
        </div>
        <Button 
          type="submit"
          form="checkout-form"
          disabled={checkoutLoading}
          className="w-full h-14 bg-[#4A2C0A] hover:bg-[#4A2C0A]/90 text-white font-['Poppins'] font-semibold text-base rounded-full active:scale-95 transition-all flex items-center justify-center gap-2"
          data-testid="checkout-submit-btn"
        >
          {checkoutLoading ? (
            <>
              <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              Procesando...
            </>
          ) : (
            <>
              Confirmar Pedido <ChevronRight className="w-5 h-5" />
            </>
          )}
        </Button>
        
        {/* Botón de ayuda */}
        <p className="text-center text-xs text-[#4A2C0A]/60 mt-3 font-['Poppins']">
          ¿Tienes dudas? <a 
            href="https://wa.me/573196536874?text=Hola,%20necesito%20ayuda%20con%20mi%20pedido"
            target="_blank"
            rel="noopener noreferrer"
            className="text-[#25D366] font-medium underline"
          >
            Escríbenos por WhatsApp
          </a>
        </p>
      </div>
      
      {/* Botón flotante de WhatsApp - Ayuda - Posicionado más arriba para no tapar Confirmar Pedido */}
      <a
        href="https://wa.me/573196536874?text=Hola,%20necesito%20ayuda%20con%20mi%20pedido%20en%20el%20catálogo"
        target="_blank"
        rel="noopener noreferrer"
        className="fixed bottom-52 right-4 z-40 flex items-center gap-2 bg-[#25D366] text-white px-3 py-2 rounded-full shadow-lg hover:bg-[#20BD5A] active:scale-95 transition-all"
        data-testid="help-whatsapp-btn"
      >
        <MessageCircle className="w-4 h-4" />
        <span className="font-['Poppins'] text-xs font-medium">¿Ayuda?</span>
      </a>
    </div>
  );
};

// Pantalla de Confirmación + Valla Comercial
const ConfirmationScreen = ({ orderData, vendedor, config, onNewOrder }) => {
  const [copied, setCopied] = useState(false);
  
  const copyOrderNumber = () => {
    navigator.clipboard.writeText(orderData.numero_pedido);
    setCopied(true);
    toast.success("Número copiado");
    setTimeout(() => setCopied(false), 2000);
  };
  
  const getPaymentInstructions = () => {
    switch (orderData.metodo_pago) {
      case "pago_vendedor":
        return {
          title: "Pago al Vendedor",
          desc: `Ten listo $${orderData.total.toLocaleString("es-CO")}. ${vendedor?.nombre} te contactará para coordinar el pago (efectivo o transferencia).`,
          action: { label: `Contactar a ${vendedor?.nombre}`, icon: MessageCircle }
        };
      case "transferencia_baruj":
        return {
          title: "Transferencia a Baruj Shefa",
          desc: `Transfiere $${orderData.total.toLocaleString("es-CO")} a:`,
          bankInfo: {
            nequi: "+57 319 6536874",
            banco: "Banco Caja Social (Bre-B)",
            cuenta: "0091250197",
            tipo: "Ahorros",
            titular: "Baruj Shefa SAS",
            nit: "901.990.427-6"
          },
          note: `Usa el número de pedido ${orderData.numero_pedido} como referencia.`,
          action: { label: "Copiar datos bancarios", icon: Copy }
        };
      case "contra_entrega":
        return {
          title: "Pago Contra Entrega",
          desc: `Ten listo $${orderData.total.toLocaleString("es-CO")} para pagar al momento de recibir tu pedido.`,
          action: null
        };
      default:
        return { title: "Pago", desc: "", action: null };
    }
  };
  
  const paymentInfo = getPaymentInstructions();
  
  const openWhatsApp = () => {
    const message = `Hola ${vendedor?.nombre}, acabo de hacer el pedido ${orderData.numero_pedido} por $${orderData.total.toLocaleString("es-CO")}. ¿Podrías confirmarme?`;
    const phone = vendedor?.telefono?.replace(/\D/g, "") || "573196536874";
    // Track WhatsApp click
    trackWhatsAppClick('catalogo_confirmacion');
    window.open(`https://wa.me/${phone.startsWith("57") ? phone : "57" + phone}?text=${encodeURIComponent(message)}`, "_blank");
  };
  
  return (
    <div className="min-h-screen bg-[#FBF5E8]">
      {/* Header */}
      <div className="bg-[#4A2C0A] text-white py-4 px-4 text-center">
        <img src="/logo-torta-cuajada.png" alt="Baruj Shefa" className="w-12 h-12 mx-auto rounded-full mb-2" />
        <p className="font-['Playfair_Display'] text-sm">Baruj Shefa SAS</p>
      </div>
      
      {/* Confirmación */}
      <div className="max-w-lg mx-auto p-4 space-y-4">
        {/* Check animado */}
        <div className="bg-white rounded-2xl p-6 text-center border border-[#E5D4B3] shadow-lg">
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4 animate-bounce">
            <Check className="w-10 h-10 text-green-600" />
          </div>
          <h1 className="font-['Playfair_Display'] text-2xl text-[#4A2C0A] mb-2">¡Tu pedido está confirmado!</h1>
          
          {/* Número de pedido */}
          <div className="bg-[#FBF5E8] rounded-xl p-4 mt-4">
            <p className="font-['Poppins'] text-sm text-[#4A2C0A]/70 mb-1">Número de pedido</p>
            <div className="flex items-center justify-center gap-2">
              <span className="font-['Playfair_Display'] text-3xl font-bold text-[#C8902A]">
                {orderData.numero_pedido}
              </span>
              <button 
                onClick={copyOrderNumber}
                className="p-2 hover:bg-[#E5D4B3] rounded-full transition-colors"
                data-testid="copy-order-number-btn"
              >
                {copied ? <Check className="w-5 h-5 text-green-600" /> : <Copy className="w-5 h-5 text-[#4A2C0A]" />}
              </button>
            </div>
            <p className="font-['Poppins'] text-xs text-[#4A2C0A]/50 mt-2">
              Consulta tu pedido en: barujshefa.com/pedido/{orderData.numero_pedido.replace("#", "")}
            </p>
          </div>
          
          {/* Beneficio Club aplicado */}
          {orderData.descuento_club > 0 && (
            <div className="mt-3 bg-green-50 border border-green-200 rounded-xl p-3" data-testid="confirmacion-descuento-club">
              <p className="font-['Poppins'] text-sm text-green-800 font-semibold">🎁 Beneficio del Club aplicado: -${Number(orderData.descuento_club).toLocaleString("es-CO")}</p>
              <p className="font-['Poppins'] text-xs text-green-700">Total a pagar: ${Number(orderData.total).toLocaleString("es-CO")}</p>
            </div>
          )}
          
          {/* Club de la Abuela - progreso tras la compra */}
          {orderData.cliente_telefono && (
            <div className="mt-4">
              <ClubProgresoCard telefono={orderData.cliente_telefono} />
            </div>
          )}
          
          {/* Instrucciones de pago */}
          <div className="mt-4 text-left bg-[#C8902A]/10 rounded-xl p-4 border border-[#C8902A]/30">
            <h3 className="font-['Playfair_Display'] text-[#4A2C0A] font-semibold mb-2">{paymentInfo.title}</h3>
            <p className="font-['Poppins'] text-sm text-[#4A2C0A]/80">{paymentInfo.desc}</p>
            
            {paymentInfo.bankInfo && (
              <div className="mt-3 bg-white rounded-lg p-3 space-y-1 text-sm font-['Poppins']">
                {paymentInfo.bankInfo.nequi && (
                  <p className="text-green-600 font-semibold"><span className="text-[#4A2C0A]/50">Nequi:</span> {paymentInfo.bankInfo.nequi}</p>
                )}
                <p><span className="text-[#4A2C0A]/50">Banco:</span> <span className="font-medium">{paymentInfo.bankInfo.banco}</span></p>
                <p><span className="text-[#4A2C0A]/50">Cuenta:</span> <span className="font-medium">{paymentInfo.bankInfo.cuenta}</span></p>
                <p><span className="text-[#4A2C0A]/50">Tipo:</span> <span className="font-medium">{paymentInfo.bankInfo.tipo}</span></p>
                <p><span className="text-[#4A2C0A]/50">Titular:</span> <span className="font-medium">{paymentInfo.bankInfo.titular}</span></p>
                {paymentInfo.bankInfo.nit && (
                  <p><span className="text-[#4A2C0A]/50">NIT:</span> <span className="font-medium">{paymentInfo.bankInfo.nit}</span></p>
                )}
              </div>
            )}
            
            {paymentInfo.note && (
              <p className="font-['Poppins'] text-xs text-[#C8902A] mt-2 font-medium">{paymentInfo.note}</p>
            )}
            
            {paymentInfo.action && (
              <Button 
                onClick={openWhatsApp}
                className="w-full mt-3 bg-[#25D366] hover:bg-[#128C7E] text-white rounded-full"
              >
                <paymentInfo.action.icon className="w-4 h-4 mr-2" /> {paymentInfo.action.label}
              </Button>
            )}
          </div>
          
          {/* Total */}
          <div className="mt-4 flex justify-between items-center p-3 bg-[#FBF5E8] rounded-xl">
            <span className="font-['Poppins'] text-[#4A2C0A]">Total a pagar</span>
            <span className="font-['Playfair_Display'] text-2xl font-bold text-[#C8902A]">
              ${orderData.total.toLocaleString("es-CO")}
            </span>
          </div>
        </div>
        
        {/* VALLA COMERCIAL */}
        
        {/* Módulo A - Frase de marca */}
        <div className="bg-[#4A2C0A] rounded-2xl p-6 text-center">
          <p className="font-['Playfair_Display'] text-[#C8902A] text-xl italic">
            &ldquo;Tradición, sabor y amor en cada bocado&rdquo;
          </p>
          <p className="font-['Poppins'] text-white/80 text-sm mt-2">Torta de Cuajada — Receta de la Abuela</p>
          <img src="/logo-torta-cuajada.png" alt="Logo" className="w-16 h-16 mx-auto mt-4 rounded-full" />
        </div>
        
        {/* Módulo B - Invitación a ser vendedor */}
        <div className="bg-gradient-to-r from-[#C8902A] to-[#D4A84B] rounded-2xl p-6 text-white">
          <Users className="w-10 h-10 mb-3" />
          <h3 className="font-['Playfair_Display'] text-xl font-bold mb-2">¿Quieres ganar con nosotros?</h3>
          <p className="font-['Poppins'] text-sm text-white/90 mb-4">
            Únete a nuestra red de vendedores. Sin inventario. Sin horario. Gana comisiones por cada venta.
          </p>
          <Button 
            onClick={() => window.location.href = "/registro-vendedor"}
            className="bg-[#4A2C0A] hover:bg-[#4A2C0A]/90 text-white font-['Poppins'] rounded-full"
          >
            Quiero ser vendedor <ChevronRight className="w-4 h-4 ml-1" />
          </Button>
        </div>
        
        {/* Módulo C - Contacto */}
        <div className="bg-white rounded-2xl p-6 border border-[#E5D4B3]">
          <h3 className="font-['Playfair_Display'] text-[#4A2C0A] font-semibold mb-4 text-center">Síguenos</h3>
          <div className="flex justify-center gap-4">
            <a href="https://wa.me/573196536874" target="_blank" rel="noopener noreferrer" className="w-12 h-12 bg-[#25D366] rounded-full flex items-center justify-center text-white hover:scale-110 transition-transform">
              <MessageCircle className="w-6 h-6" />
            </a>
            <a href="https://instagram.com/tort.adecuajada" target="_blank" rel="noopener noreferrer" className="w-12 h-12 bg-gradient-to-br from-purple-500 via-pink-500 to-orange-400 rounded-full flex items-center justify-center text-white hover:scale-110 transition-transform">
              <Instagram className="w-6 h-6" />
            </a>
            <a href="https://www.tiktok.com/@tortadecuajada" target="_blank" rel="noopener noreferrer" className="w-12 h-12 bg-black rounded-full flex items-center justify-center text-white hover:scale-110 transition-transform" data-testid="tiktok-link">
              <svg className="w-6 h-6" viewBox="0 0 24 24" fill="currentColor">
                <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
              </svg>
            </a>
            <a href="https://barujshefa.com" target="_blank" rel="noopener noreferrer" className="w-12 h-12 bg-[#4A2C0A] rounded-full flex items-center justify-center text-white hover:scale-110 transition-transform">
              <ExternalLink className="w-6 h-6" />
            </a>
          </div>
          <p className="font-['Poppins'] text-xs text-[#4A2C0A]/50 text-center mt-4">
            Baruj Shefa SAS · NIT 901.990.427-6
          </p>
        </div>
        
        {/* Nuevo pedido */}
        <Button 
          onClick={onNewOrder}
          variant="outline"
          className="w-full rounded-full border-[#4A2C0A] text-[#4A2C0A] font-['Poppins']"
        >
          Hacer otro pedido
        </Button>
      </div>
    </div>
  );
};

// Página Principal del Catálogo
export default function CatalogPage() {
  const { sellerCode, affiliateCode } = useParams();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [vendedor, setVendedor] = useState(null);
  const [productos, setProductos] = useState([]);
  const [categorias, setCategorias] = useState([]);
  const [config, setConfig] = useState(null);
  const [categoriaActiva, setCategoriaActiva] = useState("Todos");
  const [cart, setCart] = useState([]);
  const [activeTab, setActiveTab] = useState("catalog"); // Para móvil
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [orderConfirmed, setOrderConfirmed] = useState(null);
  const [embajador, setEmbajador] = useState(null); // Track ambassador referral
  const [ciudadesDisponibles, setCiudadesDisponibles] = useState([]); // Available cities for shipping
  
  // Estado del formulario movido al padre para persistir entre cambios de tabs
  const [checkoutForm, setCheckoutForm] = useState({
    cliente_nombre: "",
    cliente_telefono: "",
    beneficio_codigo: "",
    tipo_entrega: "domicilio",
    ciudad: "",
    departamento: "",
    costo_domicilio: 0,
    direccion_entrega: "",
    barrio_entrega: "",
    notas_entrega: "",
    fecha_entrega: "",
    franja_horaria: "manana",
    metodo_pago: "efectivo_vendedor",
    referencia_pago: "",
    notas: "",
    requiere_factura: false,
    factura: {
      tipo_persona: "natural",
      tipo_documento: "CC",
      numero_documento: "",
      dv: "",
      razon_social: "",
      email_facturacion: "",
      telefono_facturacion: "",
      direccion_fiscal: "",
      ciudad_fiscal: "",
      responsable_iva: "no_responsable"
    }
  });
  
  // Load available cities for shipping
  useEffect(() => {
    const fetchCiudades = async () => {
      try {
        const res = await fetch(`${API_URL}/api/zonas-envio/ciudades-disponibles`);
        const data = await res.json();
        if (res.ok && data.ciudades) {
          setCiudadesDisponibles(data.ciudades);
          // Set Bogotá as default if available
          const bogota = data.ciudades.find(c => c.ciudad.toLowerCase() === "bogotá");
          if (bogota) {
            setCheckoutForm(prev => ({
              ...prev,
              ciudad: bogota.ciudad,
              departamento: bogota.departamento,
              costo_domicilio: bogota.costo_domicilio
            }));
          }
        }
      } catch (err) {
        console.error("Error fetching ciudades:", err);
      }
    };
    fetchCiudades();
  }, []);
  
  // Track embajador click if coming from affiliate link
  useEffect(() => {
    const trackAffiliateClick = async () => {
      if (affiliateCode) {
        try {
          const res = await fetch(`${API_URL}/api/embajadores/track/${affiliateCode}`);
          const data = await res.json();
          if (data.found) {
            setEmbajador(data);
            // Store in localStorage for order attribution
            localStorage.setItem('embajador_referral', JSON.stringify({
              codigo: affiliateCode,
              nombre: data.nombre,
              timestamp: new Date().toISOString()
            }));
          }
        } catch (err) {
          console.error("Error tracking affiliate:", err);
        }
      } else {
        // Check if there's a stored referral (cookie-like behavior)
        const stored = localStorage.getItem('embajador_referral');
        if (stored) {
          try {
            const data = JSON.parse(stored);
            // Only use if less than 7 days old
            const storedDate = new Date(data.timestamp);
            const now = new Date();
            const daysDiff = (now - storedDate) / (1000 * 60 * 60 * 24);
            if (daysDiff < 7) {
              setEmbajador({ nombre: data.nombre, codigo: data.codigo });
            } else {
              localStorage.removeItem('embajador_referral');
            }
          } catch {
            localStorage.removeItem('embajador_referral');
          }
        }
      }
    };
    
    trackAffiliateClick();
  }, [affiliateCode]);
  
  // Cargar datos del catálogo
  useEffect(() => {
    const fetchCatalog = async () => {
      // Determine which code to use - for affiliate links, use the main catalog
      const catalogCode = sellerCode || (affiliateCode ? 'baruj-principal' : null);
      
      if (!catalogCode) {
        setError("Catálogo no especificado");
        setLoading(false);
        return;
      }
      
      try {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 15000); // 15s timeout
        
        const res = await fetch(`${API_URL}/api/catalogo/${catalogCode}`, {
          signal: controller.signal
        });
        clearTimeout(timeoutId);
        
        if (!res.ok) {
          if (res.status === 404) {
            setError("Catálogo no encontrado");
            return;
          }
          try {
            const errorData = await res.json();
            setError(errorData.detail || "Error al cargar el catálogo");
          } catch {
            setError("Error al cargar el catálogo");
          }
          return;
        }
        
        const data = await res.json();
        setVendedor(data.vendedor);
        setProductos(data.productos);
        setCategorias(["Todos", ...data.categorias]);
        setConfig(data.config);
        
        // Track catalog view
        trackCatalogView(data.vendedor?.nombre || 'Catálogo', sellerCode);
      } catch (err) {
        console.error("Error loading catalog:", err);
        // Handle abort error
        if (err.name === 'AbortError') {
          setError("Tiempo de espera agotado. Por favor intenta de nuevo.");
          return;
        }
        // Handle network errors
        if (err instanceof TypeError && err.message.includes('fetch')) {
          setError("Error de conexión. Verifica tu internet.");
          return;
        }
        // Generic error - don't show technical details
        setError("No se pudo cargar el catálogo. Intenta más tarde.");
      } finally {
        setLoading(false);
      }
    };
    
    if (sellerCode) fetchCatalog();
  }, [sellerCode]);
  
  // Funciones del carrito
  const addToCart = useCallback((producto) => {
    setCart((prev) => {
      const existing = prev.find((item) => item.id === producto.id);
      if (existing) {
        return prev.map((item) =>
          item.id === producto.id ? { ...item, cantidad: item.cantidad + 1 } : item
        );
      }
      return [...prev, { ...producto, cantidad: 1 }];
    });
    
    // Track AddToCart event
    trackAddToCart({
      content_name: producto.nombre,
      content_ids: producto.id,
      value: producto.precio,
      quantity: 1,
    });
    
    // Vibración en móvil
    if (navigator.vibrate) navigator.vibrate(50);
    toast.success(`${producto.nombre} agregado`);
  }, []);
  
  const updateQuantity = useCallback((id, cantidad) => {
    if (cantidad <= 0) {
      setCart((prev) => prev.filter((item) => item.id !== id));
    } else {
      setCart((prev) =>
        prev.map((item) => (item.id === id ? { ...item, cantidad } : item))
      );
    }
  }, []);
  
  const removeItem = useCallback((id) => {
    setCart((prev) => prev.filter((item) => item.id !== id));
  }, []);
  
  const getCartQuantity = (productId) => {
    const item = cart.find((item) => item.id === productId);
    return item ? item.cantidad : 0;
  };
  
  // Checkout
  const handleCheckout = async (form, total) => {
    setCheckoutLoading(true);
    
    // Track InitiateCheckout
    const contents = cart.map((item) => ({
      id: item.id,
      quantity: item.cantidad,
      item_price: item.precio,
    }));
    trackInitiateCheckout({
      value: total,
      num_items: cart.reduce((sum, item) => sum + item.cantidad, 0),
      contents,
    });
    
    try {
      // Get embajador referral if exists
      let embajadorData = null;
      const storedReferral = localStorage.getItem('embajador_referral');
      if (storedReferral) {
        try {
          embajadorData = JSON.parse(storedReferral);
        } catch (e) {
          // Invalid JSON, ignore
        }
      }
      
      const orderData = {
        seller_code: sellerCode || 'baruj-principal',
        items: cart.map((item) => ({
          producto_id: item.id,
          producto_nombre: item.nombre,
          cantidad: item.cantidad,
          precio_unitario: item.precio,
          subtotal: item.precio * item.cantidad
        })),
        ...form,
        // Include embajador attribution
        embajador_codigo: embajadorData?.codigo || null,
        requiere_factura: form.requiere_factura || false,
        datos_facturacion: form.requiere_factura ? form.factura : null,
      };
      
      const res = await fetch(`${API_URL}/api/catalogo/orders`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(orderData)
      });
      
      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(errorData.detail || "Error al crear el pedido");
      }
      
      const data = await res.json();
      
      // Track Purchase event on success
      trackPurchase({
        value: total,
        contents,
        num_items: cart.reduce((sum, item) => sum + item.cantidad, 0),
        order_id: data.numero_pedido,
      });
      
      setOrderConfirmed({ ...data, cliente_telefono: form.cliente_telefono });
      setCart([]);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setCheckoutLoading(false);
    }
  };
  
  // Filtrar productos por categoría
  const productosFiltrados = categoriaActiva === "Todos" 
    ? productos 
    : productos.filter((p) => p.categoria === categoriaActiva);
  
  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-[#FBF5E8] flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-[#C8902A] border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="font-['Poppins'] text-[#4A2C0A]">Cargando catálogo...</p>
        </div>
      </div>
    );
  }
  
  // Error state
  if (error) {
    // Clean error message for display - don't show technical errors to users
    const displayError = error.includes("postMessage") || error.includes("cloned") || error.includes("fetch")
      ? "Error de conexión. Por favor intenta de nuevo."
      : (error === "Catálogo no encontrado" ? error : "El catálogo no está disponible en este momento.");
    
    return (
      <div className="min-h-screen bg-[#FBF5E8] flex items-center justify-center p-4">
        <div className="text-center max-w-md">
          <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <span className="text-4xl">😕</span>
          </div>
          <h1 className="font-['Playfair_Display'] text-2xl text-[#4A2C0A] mb-2">{displayError}</h1>
          <p className="font-['Poppins'] text-[#4A2C0A]/70 mb-4">
            El catálogo que buscas no está disponible.
          </p>
          <div className="flex gap-3 justify-center">
            <Button onClick={() => window.location.reload()} variant="outline" className="rounded-full">
              Reintentar
            </Button>
            <Button onClick={() => navigate("/")} className="bg-[#4A2C0A] rounded-full">
              Ir al inicio
            </Button>
          </div>
        </div>
      </div>
    );
  }
  
  // Confirmation screen
  if (orderConfirmed) {
    return (
      <ConfirmationScreen 
        orderData={orderConfirmed}
        vendedor={vendedor}
        config={config}
        onNewOrder={() => {
          setOrderConfirmed(null);
          setActiveTab("catalog");
        }}
      />
    );
  }
  
  const totalItems = cart.reduce((sum, item) => sum + item.cantidad, 0);
  
  return (
    <div className="min-h-screen bg-[#FBF5E8]">
      {/* Embajador Banner */}
      {embajador && (
        <div className="bg-gradient-to-r from-amber-500 to-orange-500 text-white py-2 px-4 text-center">
          <p className="text-sm font-medium">
            Has llegado recomendado por <span className="font-bold">{embajador.nombre}</span> 💛
          </p>
        </div>
      )}
      
      {/* DESKTOP: Split-screen */}
      <div className="hidden md:grid md:grid-cols-2 min-h-screen">
        {/* Left: Catálogo */}
        <div className="overflow-y-auto border-r border-[#E5D4B3]">
          {/* Header vendedor */}
          <div className="sticky top-0 bg-[#FBF5E8] z-10 p-6 border-b border-[#E5D4B3]">
            <div className="flex items-center gap-4">
              <img 
                src="/logo-torta-cuajada.png" 
                alt="Baruj Shefa" 
                className="w-14 h-14 rounded-full border-2 border-[#C8902A]"
              />
              <div>
                <p className="font-['Poppins'] text-xs text-[#C8902A] uppercase tracking-wider">Catálogo de</p>
                <h1 className="font-['Playfair_Display'] text-xl text-[#4A2C0A] font-semibold">{vendedor?.nombre}</h1>
              </div>
            </div>
            {vendedor?.mensaje_catalogo && (
              <p className="font-['Poppins'] text-sm text-[#4A2C0A]/70 italic mt-3">
                &ldquo;{vendedor.mensaje_catalogo}&rdquo;
              </p>
            )}
            
            {/* Categorías */}
            <div className="flex gap-2 mt-4 overflow-x-auto pb-2 scrollbar-hide">
              {categorias.map((cat) => (
                <button
                  key={cat}
                  onClick={() => setCategoriaActiva(cat)}
                  className={`px-4 py-2 rounded-full whitespace-nowrap font-['Poppins'] text-sm transition-all ${
                    categoriaActiva === cat
                      ? "bg-[#4A2C0A] text-[#FBF5E8]"
                      : "bg-transparent border border-[#E5D4B3] text-[#4A2C0A] hover:border-[#C8902A]"
                  }`}
                >
                  {cat}
                </button>
              ))}
            </div>
          </div>
          
          {/* Grid de productos */}
          <div className="p-6 grid grid-cols-2 gap-4">
            {productosFiltrados.map((producto) => (
              <ProductCard
                key={producto.id}
                producto={producto}
                cantidad={getCartQuantity(producto.id)}
                onAdd={addToCart}
                onUpdate={updateQuantity}
              />
            ))}
          </div>
        </div>
        
        {/* Right: Carrito (sticky) */}
        <div className="sticky top-0 h-screen overflow-hidden bg-white flex flex-col">
          <div className="p-4 border-b border-[#E5D4B3] bg-white flex-shrink-0">
            <h2 className="font-['Playfair_Display'] text-xl text-[#4A2C0A] flex items-center gap-2">
              <ShoppingCart className="w-5 h-5 text-[#C8902A]" />
              Tu Pedido
              {totalItems > 0 && (
                <span className="bg-[#C8902A] text-white text-xs font-['Poppins'] px-2 py-0.5 rounded-full">
                  {totalItems}
                </span>
              )}
            </h2>
          </div>
          <div className="flex-1 overflow-hidden">
            <CartPanel
              cart={cart}
              updateQuantity={updateQuantity}
              removeItem={removeItem}
              config={config}
              vendedor={vendedor}
              onCheckout={handleCheckout}
              checkoutLoading={checkoutLoading}
              form={checkoutForm}
              setForm={setCheckoutForm}
              ciudadesDisponibles={ciudadesDisponibles}
            />
          </div>
        </div>
      </div>
      
      {/* MOBILE: Tabs */}
      <div className="md:hidden">
        {/* Header móvil */}
        <div className="sticky top-0 bg-[#FBF5E8] z-20 p-4 border-b border-[#E5D4B3]">
          <div className="flex items-center gap-3">
            <img 
              src="/logo-torta-cuajada.png" 
              alt="Baruj Shefa" 
              className="w-10 h-10 rounded-full border-2 border-[#C8902A]"
            />
            <div className="flex-1 min-w-0">
              <p className="font-['Poppins'] text-[10px] text-[#C8902A] uppercase tracking-wider">Catálogo de</p>
              <h1 className="font-['Playfair_Display'] text-base text-[#4A2C0A] font-semibold truncate">{vendedor?.nombre}</h1>
            </div>
          </div>
        </div>
        
        {/* Contenido según tab */}
        {activeTab === "catalog" ? (
          <div className="pb-20">
            {/* Categorías */}
            <div className="sticky top-[72px] bg-[#FBF5E8] z-10 px-4 py-3 overflow-x-auto scrollbar-hide flex gap-2">
              {categorias.map((cat) => (
                <button
                  key={cat}
                  onClick={() => setCategoriaActiva(cat)}
                  className={`px-3 py-1.5 rounded-full whitespace-nowrap font-['Poppins'] text-xs transition-all ${
                    categoriaActiva === cat
                      ? "bg-[#4A2C0A] text-[#FBF5E8]"
                      : "bg-transparent border border-[#E5D4B3] text-[#4A2C0A]"
                  }`}
                >
                  {cat}
                </button>
              ))}
            </div>
            
            {/* Grid productos móvil */}
            <div className="p-4 grid grid-cols-2 gap-3">
              {productosFiltrados.map((producto) => (
                <ProductCard
                  key={producto.id}
                  producto={producto}
                  cantidad={getCartQuantity(producto.id)}
                  onAdd={addToCart}
                  onUpdate={updateQuantity}
                />
              ))}
            </div>
            
            {/* Mobile Quick Actions - Below Products */}
            <div className="px-4 pb-6 space-y-3">
              {/* Divider */}
              <div className="flex items-center gap-3 py-2">
                <div className="flex-1 h-px bg-[#E5D4B3]"></div>
                <span className="font-['Poppins'] text-xs text-[#4A2C0A]/50 uppercase tracking-wider">Más opciones</span>
                <div className="flex-1 h-px bg-[#E5D4B3]"></div>
              </div>
              
              {/* Club de la Abuela - activo */}
              <a href="/club" data-testid="mobile-club-link" className="block bg-gradient-to-br from-[#C8902A]/10 to-[#C8902A]/5 rounded-xl p-4 border border-[#C8902A]/20 hover:border-[#C8902A]/50 transition-colors">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-[#C8902A]/20 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Star className="w-5 h-5 text-[#C8902A]" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <h4 className="font-['Playfair_Display'] text-[#4A2C0A] font-semibold text-sm">
                        Club de la Abuela
                      </h4>
                      <span className="text-[8px] bg-green-600 text-white px-1.5 py-0.5 rounded-full font-['Poppins'] uppercase">
                        Nuevo
                      </span>
                    </div>
                    <p className="font-['Poppins'] text-[#4A2C0A]/70 text-xs">
                      Consulta tu nivel y tus beneficios con tu celular
                    </p>
                  </div>
                </div>
              </a>
              
              {/* Become a Seller */}
              <a 
                href="/registro-vendedor"
                className="block bg-[#4A2C0A] hover:bg-[#4A2C0A]/90 rounded-xl p-4 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-white/10 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Users className="w-5 h-5 text-white" />
                  </div>
                  <div className="flex-1">
                    <h4 className="font-['Playfair_Display'] text-white font-semibold text-sm">
                      ¿Quieres vender tortas?
                    </h4>
                    <p className="font-['Poppins'] text-white/70 text-xs">
                      Únete como vendedor Baruj Shefa
                    </p>
                  </div>
                  <ChevronRight className="w-5 h-5 text-white/70" />
                </div>
              </a>
              
              {/* Social Media */}
              <div className="bg-white rounded-xl p-4 border border-[#E5D4B3]">
                <h4 className="font-['Playfair_Display'] text-[#4A2C0A] font-semibold text-sm mb-3 text-center">
                  Síguenos en redes sociales
                </h4>
                <div className="flex items-center justify-center gap-4">
                  <a 
                    href="https://www.instagram.com/tortadecuajada/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-11 h-11 bg-gradient-to-br from-purple-500 via-pink-500 to-orange-400 rounded-xl flex items-center justify-center text-white active:scale-95 transition-transform"
                  >
                    <Instagram className="w-5 h-5" />
                  </a>
                  <a 
                    href="https://www.tiktok.com/@tortadecuajada"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-11 h-11 bg-black rounded-xl flex items-center justify-center text-white active:scale-95 transition-transform"
                  >
                    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
                    </svg>
                  </a>
                  <a 
                    href="https://wa.me/573196536874"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-11 h-11 bg-green-500 rounded-xl flex items-center justify-center text-white active:scale-95 transition-transform"
                  >
                    <MessageCircle className="w-5 h-5" />
                  </a>
                </div>
                <p className="font-['Poppins'] text-[#4A2C0A]/50 text-[10px] text-center mt-2">
                  @tortadecuajada
                </p>
              </div>
            </div>
          </div>
        ) : (
          <div className="pb-20">
            <CartPanel
              cart={cart}
              updateQuantity={updateQuantity}
              removeItem={removeItem}
              config={config}
              vendedor={vendedor}
              onCheckout={handleCheckout}
              checkoutLoading={checkoutLoading}
              form={checkoutForm}
              setForm={setCheckoutForm}
              ciudadesDisponibles={ciudadesDisponibles}
            />
          </div>
        )}
        
        {/* Bottom tabs móvil */}
        <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-[#E5D4B3] z-30">
          <div className="flex">
            <button
              onClick={() => setActiveTab("catalog")}
              className={`flex-1 py-3 flex flex-col items-center gap-1 transition-colors ${
                activeTab === "catalog" ? "text-[#C8902A]" : "text-[#4A2C0A]/50"
              }`}
            >
              <ClipboardList className="w-5 h-5" />
              <span className="font-['Poppins'] text-xs">Catálogo</span>
            </button>
            <button
              onClick={() => setActiveTab("cart")}
              className={`flex-1 py-3 flex flex-col items-center gap-1 relative transition-colors ${
                activeTab === "cart" ? "text-[#C8902A]" : "text-[#4A2C0A]/50"
              }`}
            >
              <ShoppingCart className="w-5 h-5" />
              {totalItems > 0 && (
                <span className="absolute top-2 right-1/2 translate-x-4 bg-[#C8902A] text-white text-[10px] font-bold w-5 h-5 rounded-full flex items-center justify-center animate-bounce">
                  {totalItems}
                </span>
              )}
              <span className="font-['Poppins'] text-xs">Mi Pedido</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
